/**
 * @file QuestionInput.tsx
 * @description This file defines a reusable input component for users to type questions.
 * It includes features like a send button, optional speech-to-text input, and handling for
 * authentication state to disable input when login is required but the user is not logged in.
 */

import { useState, useEffect, useContext } from "react";
import { Stack, TextField } from "@fluentui/react";
import { Button, Tooltip } from "@fluentui/react-components";
import { Send28Filled } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";

import styles from "./QuestionInput.module.css";
import { SpeechInput } from "./SpeechInput";
import { LoginContext } from "../../loginContext";
import { requireLogin } from "../../authConfig";

/**
 * @interface Props
 * @description Defines the properties for the QuestionInput component.
 */
interface Props {
    /** Function to call when the user sends a question. */
    onSend: (question: string) => void;
    /** Whether the input should be disabled (e.g., while an answer is being generated). */
    disabled: boolean;
    /** An initial question to populate the input field with. */
    initQuestion?: string;
    /** Placeholder text for the input field. */
    placeholder?: string;
    /** If true, the input field will be cleared after sending a question. */
    clearOnSend?: boolean;
    /** If true, the speech-to-text input button will be displayed. */
    showSpeechInput?: boolean;
}

/**
 * @component QuestionInput
 * @description A controlled component for text input that allows users to ask questions.
 * It handles sending the question, keyboard events (Enter to send), and integrates
 * with an optional speech input component.
 */
export const QuestionInput = ({ onSend, disabled, placeholder, clearOnSend, initQuestion, showSpeechInput }: Props) => {
    // State to hold the current value of the text input.
    const [question, setQuestion] = useState<string>("");
    // Accessing login state to conditionally disable the input.
    const { loggedIn } = useContext(LoginContext);
    // Hook for internationalization.
    const { t } = useTranslation();
    // State to track if the user is in the middle of composing with an IME (e.g., for CJK languages).
    const [isComposing, setIsComposing] = useState(false);

    // Effect to set the initial question if provided.
    useEffect(() => {
        initQuestion && setQuestion(initQuestion);
    }, [initQuestion]);

    /**
     * @function sendQuestion
     * @description Triggers the onSend callback with the current question and clears the input if configured.
     */
    const sendQuestion = () => {
        if (disabled || !question.trim()) {
            return;
        }

        onSend(question);

        if (clearOnSend) {
            setQuestion("");
        }
    };

    /**
     * @function onEnterPress
     * @description Handles the keydown event to send the question when Enter is pressed (without Shift).
     * @param {React.KeyboardEvent<Element>} ev - The keyboard event.
     */
    const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
        // Do not send if the user is composing with an IME.
        if (isComposing) return;

        if (ev.key === "Enter" && !ev.shiftKey) {
            ev.preventDefault();
            sendQuestion();
        }
    };

    /**
     * @function handleCompositionStart
     * @description Sets the isComposing flag to true when an IME composition starts.
     */
    const handleCompositionStart = () => {
        setIsComposing(true);
    };
    /**
     * @function handleCompositionEnd
     * @description Sets the isComposing flag to false when an IME composition ends.
     */
    const handleCompositionEnd = () => {
        setIsComposing(false);
    };

    /**
     * @function onQuestionChange
     * @description Updates the question state as the user types, with a length limit.
     */
    const onQuestionChange = (_ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        if (!newValue) {
            setQuestion("");
        } else if (newValue.length <= 1000) {
            setQuestion(newValue);
        }
    };

    // Determine if the input should be disabled due to access control requirements.
    const disableRequiredAccessControl = requireLogin() && !loggedIn;
    // The send button is disabled if the parent is disabled, the input is empty, or access is denied.
    const sendQuestionDisabled = disabled || !question.trim() || disableRequiredAccessControl;

    // Override placeholder text if the user needs to log in.
    if (disableRequiredAccessControl) {
        placeholder = "Please login to continue...";
    }

    return (
        <Stack horizontal className={styles.questionInputContainer}>
            {/* The main text input area */}
            <TextField
                className={styles.questionInputTextArea}
                disabled={disableRequiredAccessControl}
                placeholder={placeholder}
                multiline
                resizable={false}
                borderless
                value={question}
                onChange={onQuestionChange}
                onKeyDown={onEnterPress}
                onCompositionStart={handleCompositionStart}
                onCompositionEnd={handleCompositionEnd}
            />
            {/* Container for the send button */}
            <div className={styles.questionInputButtonsContainer}>
                <Tooltip content={t("tooltips.submitQuestion")} relationship="label">
                    <Button size="large" icon={<Send28Filled primaryFill="rgba(115, 118, 225, 1)" />} disabled={sendQuestionDisabled} onClick={sendQuestion} />
                </Tooltip>
            </div>
            {/* Conditionally render the speech input component */}
            {showSpeechInput && <SpeechInput updateQuestion={setQuestion} />}
        </Stack>
    );
};
